// The slash menu's listbox, shared by the Lexical SlashMenuPlugin and the Skia overlay: items grouped by section, the
// highlighted option `aria-selected`, each option chosen by click or Enter/Space on it.
import { groupSlashItems, type SlashMenuSection } from './core/slash-menu'
import { Button } from '@w/ui/components/button'
import {
  CodeXmlIcon, CopyIcon, FileIcon, FileSpreadsheetIcon, FileTextIcon, HashIcon, ImageIcon, InfoIcon, LayoutIcon, ListIcon, ListTodoIcon,
  MessageSquareIcon, MicIcon, MinusIcon, Share2Icon, SquareIcon, TextIcon, ToggleLeftIcon, VideoIcon, YoutubeIcon,
} from '@w/ui/components/icon'
import type { CSSProperties, ReactNode, Ref } from 'react'

export interface SlashMenuListProps {
  testId: string
  /** `icon`: a host item's own icon; built-in items get theirs by title. */
  items: readonly { title: string; section: SlashMenuSection; icon?: ReactNode }[]
  selectedIndex: number | null
  onChoose: (index: number) => void
  onHighlight: (index: number) => void
  /** The option element of `index` (Lexical scrolls the highlighted option into view through it). */
  itemRef?: (index: number) => Ref<HTMLButtonElement>
  style?: CSSProperties
}

function toTestIdSegment(value: string) {
  return value.toLowerCase().trim().replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/^-+|-+$/g, '') || 'item'
}

export function SlashMenuList({ testId, items, selectedIndex, onChoose, onHighlight, itemRef, style }: SlashMenuListProps) {
  return (
    <div
      className="border-border bg-surface-2 relative z-[1000] max-h-72 w-64 overflow-y-auto rounded-xl border p-1.5 shadow-xl"
      data-testid={testId}
      id="typeahead-menu"
      role="listbox"
      style={style}
    >
      {groupSlashItems(items).map(({ section, items: grouped }) => (
        <div aria-label={section} className="not-first:mt-1" key={section} role="group">
          <div aria-hidden className="text-on-surface-secondary px-2 py-1 text-[11px] font-medium tracking-wide uppercase">
            {section}
          </div>
          <div className="m-0 flex flex-col gap-0.5 p-0">
            {grouped.map(({ index, item }) => (
              <Button
                className="text-on-surface hover:bg-primary/8 aria-selected:bg-primary/8 h-8 w-full justify-start rounded-md px-2 text-left text-sm font-normal [&>span]:w-full [&>span]:justify-start"
                key={item.title}
                ref={itemRef?.(index)}
                role="option"
                aria-selected={selectedIndex === index}
                id={`typeahead-item-${index}`}
                data-testid={`${testId}-item-${toTestIdSegment(item.title)}-${index}`}
                semanticRole="secondary"
                size="sm"
                type="button"
                variant="text"
                onMouseEnter={() => {
                  onHighlight(index)
                }}
                onClick={() => {
                  onHighlight(index)
                  onChoose(index)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onChoose(index)
                }}
              >
                {item.icon ? <span aria-hidden className="text-on-surface-secondary flex size-4 shrink-0 items-center [&>svg]:size-4">{item.icon}</span> : slashMenuIcon(item.title)}
                {item.title}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function slashMenuIcon(title: string) {
  const className = 'text-on-surface-secondary size-4 shrink-0'
  if (title === 'Duplicate') return <CopyIcon className={className} />
  if (title === 'Paragraph') return <TextIcon className={className} />
  if (title === 'Page') return <FileTextIcon className={className} />
  if (title === 'Cloze') return <SquareIcon className={className} />
  if (title === 'Spoken Response') return <MicIcon className={className} />
  if (title === 'Callout') return <InfoIcon className={className} />
  if (title === 'Toggle') return <ToggleLeftIcon className={className} />
  if (title === 'Columns') return <LayoutIcon className={className} />
  if (title === 'Table') return <FileSpreadsheetIcon className={className} />
  if (title === 'Divider') return <MinusIcon className={className} />
  if (title === 'Embed') return <YoutubeIcon className={className} />
  if (title.startsWith('Heading')) return <HashIcon className={className} />
  if (title === 'Check List') return <ListTodoIcon className={className} />
  if (title.includes('List')) return <ListIcon className={className} />
  if (title === 'Code Block' || title === 'Formula') return <CodeXmlIcon className={className} />
  if (title === 'Image') return <ImageIcon className={className} />
  if (title === 'File') return <FileIcon className={className} />
  if (title === 'Video') return <VideoIcon className={className} />
  if (title === 'Link') return <Share2Icon className={className} />
  return <MessageSquareIcon className={className} />
}
